import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Form, Formik, FormikHelpers } from 'formik'
import { Button, FormControl, Grid, InputLabel, MenuItem, Select, Stack } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { Asset, AssetInput, Company } from '../types'
import { getCompanies } from '../services/companyService'
import { updateAsset } from '../services/assetService'
import { createErrorNotification, createSuccessNotification } from '../redux/slices/notifications'

export interface AssetCompanyFormProps {
  asset: Asset
  beforeSubmit?: (values: AssetInput) => void
  afterSubmit?: (asset: Asset) => void
  onCancel?: () => void
}

export default function AssetCompanyForm({
  asset,
  beforeSubmit,
  afterSubmit,
  onCancel
}: AssetCompanyFormProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('asset')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The companies state.
   */
  const [companies, setCompanies] = useState<Company[]>([])

  /**
   * The loading state.
   */
  const [loading, setLoading] = useState<boolean>(true)

  /**
   * The initial form values.
   */
  const initialValues: AssetInput = {
    name: asset.name,
    company: asset.companyId,
    project: asset.projectId,
    level: asset.levelId,
    lift: asset.liftId
  }

  /**
   * Load the companies.
   */
  async function loadCompanies(): Promise<void> {
    try {
      setLoading(true)

      setCompanies((await getCompanies()).sort((a, b) => a.name.localeCompare(b.name)))
    } catch (_) {
      // Do nothing.
    } finally {
      setLoading(false)
    }
  }

  /**
   * Handle the "submit" event.
   */
  async function handleSubmit(values: AssetInput, { setSubmitting }: FormikHelpers<AssetInput>) {
    try {
      setSubmitting(true)

      beforeSubmit?.(values)

      const updatedAsset = await updateAsset(asset.id, values)
      dispatch(createSuccessNotification(t('updateAssetSuccess')))

      setSubmitting(false)
      afterSubmit?.(updatedAsset)
    } catch (_) {
      dispatch(createErrorNotification(t('updateAssetError')))
      setSubmitting(false)
    }
  }

  /**
   * Load the companies when the component mounts.
   */
  useEffect(() => {
    loadCompanies()
  }, [])

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit}>
      {({ values, setFieldValue, setFieldTouched, isSubmitting, handleChange, handleSubmit }) => (
        <Form onSubmit={handleSubmit}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="company-select-label">{t('company')}</InputLabel>
                <Select
                  id="company-select"
                  labelId="company-select-label"
                  name="company"
                  label={t('company')}
                  value={values.company}
                  disabled={loading || isSubmitting}
                  autoFocus
                  onChange={handleChange}
                >
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
                {values.company !== null && (
                  <Stack direction="row" justifyContent="flex-end" alignItems="center">
                    <Button
                      variant="text"
                      color="inherit"
                      size="small"
                      disabled={isSubmitting}
                      sx={{ mt: 1 }}
                      onClick={() => {
                        setFieldValue('company', null)
                        setFieldTouched('company', true)
                      }}
                    >
                      {t('clearSelection')}
                    </Button>
                  </Stack>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Stack direction="row" justifyContent="flex-end" spacing={2}>
                <Button variant="text" color="inherit" disabled={isSubmitting} onClick={onCancel}>
                  {t('cancel')}
                </Button>

                <LoadingButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  loading={isSubmitting}
                >
                  {t('save')}
                </LoadingButton>
              </Stack>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}
